import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getLotteryPacks } from 'services/scratcherService';
import type {
  CurrentInventoryDetailRow,
  CurrentInventoryStatusParams,
  CurrentInventoryStatusResponse,
  LotteryPackSnapshotResponse,
  LotteryPackSnapshotRow,
} from '../../types';

const emptyCurrentInventoryStatus = (storeId = ''): CurrentInventoryStatusResponse => ({
  metadata: {
    store_id: storeId,
    as_of_date: null,
    source: null,
    summary_row_count: 0,
    detail_row_count: 0,
  },
  summary: [],
  details: [],
});

const statusLabels: Record<LotteryPackSnapshotRow['status'], string> = {
  issued: 'In Transit',
  received: 'Received',
  activated: 'Activated',
  settled: 'Settled',
  returned: 'Returned',
};

const toNumericTotal = (value: number | string | null | undefined) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const capitalizeFirstLetter = (value: string | null | undefined) => {
  const text = String(value ?? '').trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : null;
};

const toPackIdString = (value: string | number | null | undefined) =>
  String(value ?? '').trim();

const completedDateForPack = (pack: LotteryPackSnapshotRow) => {
  if (pack.status === 'returned' && (pack.tickets_remaining ?? 0) <= 0) {
    return pack.effective_return_date || pack.return_date || pack.settled_date || pack.modified_date;
  }
  if (pack.is_sold_out) {
    return pack.source_audit_created_at || pack.settled_date || pack.activated_date || pack.modified_date;
  }
  return null;
};

const subgroupForPack = (pack: LotteryPackSnapshotRow): string | null => {
  if (pack.status === 'returned' && (pack.tickets_remaining ?? 0) <= 0) {
    return 'Returned';
  }
  if (pack.is_sold_out) {
    return 'Sold Out';
  }
  return capitalizeFirstLetter(pack.location_type);
};

export const mapPackToInventoryRow = (pack: LotteryPackSnapshotRow): CurrentInventoryDetailRow => {
  const status = statusLabels[pack.status];
  const subgroup = subgroupForPack(pack);
  const rawPackId = toPackIdString(pack.pack_id);
  const displayPackId = toPackIdString(pack.display_pack_id) || rawPackId;
  return {
    game_id: pack.game_id,
    game_name: pack.game_name,
    pack_id: displayPackId,
    raw_pack_id: rawPackId,
    display_pack_id: displayPackId,
    status,
    subgroup,
    lottery_status_date: (
      pack.status === 'returned'
        ? pack.effective_return_date || pack.return_date || pack.settled_date || pack.modified_date
        : pack.status === 'settled'
          ? pack.settled_date || pack.modified_date
          : pack.status === 'activated'
            ? pack.activated_date || pack.modified_date
            : pack.status === 'received'
              ? pack.confirmed_date || pack.issued_date || pack.modified_date
              : pack.issued_date || pack.modified_date
    ),
    activated_date: pack.activated_date,
    completed_date: completedDateForPack(pack),
    completed_type: subgroup === 'Returned' ? 'Returned' : pack.is_sold_out ? 'Sold Out' : null,
    tickets_per_pack: pack.tickets_per_pack,
    rate: toNumericTotal(pack.price_point),
    latest_audit_ticket_number: pack.latest_audit_ticket_number,
    latest_audit_created_at: pack.source_audit_created_at,
    latest_store_audit_created_at: pack.source_audit_created_at,
    remaining_tickets: pack.tickets_remaining ?? 0,
    remaining_value: pack.remaining_value,
    pack_location: subgroup === 'Returned' || pack.is_sold_out
      ? null
      : capitalizeFirstLetter(pack.location_type),
    backstock_state: 'normal',
    returned_tickets: pack.returned_tickets,
    is_sold_out_before_inventory_tracking: pack.is_excluded_from_tracking,
    can_mark_sold_out_before_inventory_tracking: !pack.is_excluded_from_tracking,
    is_manual_sold_out: pack.is_manual_sold_out,
    can_mark_manual_sold_out: Boolean(pack.can_mark_manual_sold_out),
    backstock_last_seen_at: pack.backstock_last_seen_at ?? null,
    backstock_last_seen_ticket_number: pack.backstock_last_seen_ticket_number ?? null,
  };
};

const buildResponseFromPacks = (
  storeId: string,
  response: LotteryPackSnapshotResponse,
): CurrentInventoryStatusResponse => {
  const details = response.packs.map(mapPackToInventoryRow);
  return {
    metadata: {
      store_id: storeId,
      as_of_date: response.metadata.report_date || response.metadata.as_of_datetime?.slice(0, 10) || null,
      source: response.metadata.source,
      summary_row_count: 0,
      detail_row_count: details.length,
    },
    summary: [],
    details,
  };
};

export const filterReport = (
  report: CurrentInventoryStatusResponse,
  filters: CurrentInventoryStatusParams,
) => {
  let details = report.details;

  if (filters.sold_out_before_inventory_tracking === true) {
    details = details.filter((row) => row.is_sold_out_before_inventory_tracking);
  } else {
    details = details.filter((row) => !row.is_sold_out_before_inventory_tracking);
  }

  if (filters.manual_sold_out === true) {
    details = details.filter((row) => row.is_manual_sold_out);
  }

  if (!filters.include_sold_out && filters.manual_sold_out !== true) {
    details = details.filter((row) =>
      row.subgroup !== 'Sold Out' && row.subgroup !== 'Returned'
    );
  }

  if (filters.status) {
    details = details.filter((row) => row.status === filters.status);
  }

  if (filters.subgroup) {
    details = details.filter((row) => row.subgroup === filters.subgroup);
  }

  if (filters.game_id) {
    details = details.filter((row) => row.game_id === filters.game_id);
  }

  if (filters.pack_id) {
    const packFilter = filters.pack_id.toLowerCase();
    details = details.filter((row) => row.pack_id.toLowerCase().includes(packFilter));
  }

  return {
    ...report,
    metadata: {
      ...report.metadata,
      detail_row_count: details.length,
    },
    details,
  };
};

export function useCurrentInventoryStatus(
  storeId?: string,
  filters: CurrentInventoryStatusParams = {},
  options: { enabled?: boolean } = {}
) {
  const isEnabled = !!storeId && options.enabled !== false;

  const queryResult = useQuery<CurrentInventoryStatusResponse, Error>({
    queryKey: ['lottery-packs', storeId, filters.report_date],
    queryFn: async () => {
      if (!storeId) {
        return emptyCurrentInventoryStatus();
      }

      const response = await getLotteryPacks(storeId, {
        report_date: filters.report_date,
        include_action_eligibility: true,
      });
      if (response.success && response.data) {
        return buildResponseFromPacks(storeId, response.data);
      }

      throw new Error(response.error || 'Failed to load lottery packs');
    },
    staleTime: 1 * 60 * 1000,
    enabled: isEnabled,
  });

  const rawReport = isEnabled
    ? (queryResult.data ?? emptyCurrentInventoryStatus(storeId))
    : emptyCurrentInventoryStatus(storeId);
  const report = useMemo<CurrentInventoryStatusResponse>(() => {
    if (!isEnabled) {
      return rawReport;
    }

    return filterReport(rawReport, filters);
  }, [filters, isEnabled, rawReport]);

  return {
    report,
    metadata: report.metadata,
    summary: report.summary,
    details: report.details,
    isLoading: isEnabled ? queryResult.isLoading : false,
    isFetching: isEnabled ? queryResult.isFetching : false,
    isError: isEnabled ? queryResult.isError : false,
    error: isEnabled ? (queryResult.error ?? null) : null,
  };
}
